// ==========================================================================
// Phone capture page — collected only when a user requests a payout.
// Reachable from /payout/phone in both Mini-App and PWA modalities.
//
// The manual path is two-step:
//   1. user enters phone → we POST /user/phone/send-code → SMS arrives
//   2. user enters the 6-digit code → POST /user/phone {phone, code} →
//      backend verifies + persists.
// This was hardened in response to the PR #232 security review (a single-
// step manual claim let an authenticated user squat someone else's phone).
//
// Mini-App users also get a "Share via Telegram" CTA that deep-links into
// the bot DM where /payout runs the Telegram-signed contact-share flow
// (zero OTP needed; HMAC + user_id match prove ownership).
// ==========================================================================

import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { isTelegramWebApp } from '@/lib/platform';
import { canRequestContact, openTelegramLink, requestTelegramContact } from '@/lib/telegram';
import { useInvalidateUser } from '@/hooks/useUser';
import {
  getTelegramShareStatus,
  InvalidPhoneCodeException,
  PhoneCodeRateLimitedException,
  PhoneTakenException,
  requestPhoneCode,
  setPhoneNumber,
} from '@/services/phone.service';

const E164_REGEX = /^\+\d{8,15}$/;

type Step = 'enter_phone' | 'enter_code';

export default function PhoneCapture() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>('enter_phone');
  const [phone, setPhone] = useState('+251');
  const [code, setCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [waitingForContact, setWaitingForContact] = useState(false);
  const pollingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const invalidateUser = useInvalidateUser();

  const botUsername = (import.meta.env.VITE_TELEGRAM_BOT_USERNAME as string | undefined) ?? '';
  // One-tap path: native popup inside the Mini-App (Bot API 6.9+).
  // Older Telegram clients leave requestContact undefined; for those we
  // still expose the bot-DM deep-link below.
  const showInAppShare = canRequestContact();
  const showShareViaTelegramDM = isTelegramWebApp() && botUsername.length > 0 && !showInAppShare;

  const sanitizePhone = (raw: string) => raw.replace(/[^\d+]/g, '');

  const sendCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const sanitized = sanitizePhone(phone);
    if (!E164_REGEX.test(sanitized)) {
      setError(t('payoutPhone.invalidFormat', 'Enter a number in international format (+251…)'));
      return;
    }

    setSubmitting(true);
    try {
      await requestPhoneCode(sanitized);
      setPhone(sanitized);
      setStep('enter_code');
    } catch (err) {
      if (err instanceof PhoneTakenException) {
        setError(t('payoutPhone.phoneTaken', 'This number is already linked to another account.'));
      } else if (err instanceof PhoneCodeRateLimitedException) {
        setError(
          t('payoutPhone.rateLimited', 'Please wait {{seconds}}s before requesting another code.', {
            seconds: err.cooldown,
          })
        );
      } else {
        setError(t('payoutPhone.genericError', 'Something went wrong. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const verifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!/^\d{4,8}$/.test(code)) {
      setError(t('payoutPhone.invalidCodeFormat', 'Enter the digits from the SMS.'));
      return;
    }

    setSubmitting(true);
    try {
      await setPhoneNumber(phone, code);
      navigate('/home', { replace: true });
    } catch (err) {
      if (err instanceof InvalidPhoneCodeException) {
        setError(t('payoutPhone.invalidCode', 'That code is incorrect or has expired.'));
      } else if (err instanceof PhoneTakenException) {
        setError(t('payoutPhone.phoneTaken', 'This number is already linked to another account.'));
        setStep('enter_phone');
      } else {
        setError(t('payoutPhone.genericError', 'Something went wrong. Please try again.'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const goBackToPhoneStep = () => {
    setStep('enter_phone');
    setCode('');
    setError(null);
  };

  /**
   * One-tap Telegram share path.
   *
   * Telegram delivers WebApp.requestContact() results to the bot via a
   * webhook (message.contact), NOT back to the webview. So we can't read
   * the phone here directly — we trigger the popup, wait for the user's
   * grant, then poll the user profile for up to ~12 seconds. The
   * ContactShareHandler on the backend persists phone_number; once we
   * see it on the User row we navigate home. No OTP needed: Telegram
   * itself verifies the phone, so we trust the signed delivery path.
   */
  const handleShareTelegramContact = async () => {
    setError(null);
    setWaitingForContact(true);

    const shared = await requestTelegramContact();
    if (!shared) {
      setWaitingForContact(false);
      return;
    }

    // Cancel any prior poll loop (e.g. user tapped twice).
    if (pollingTimerRef.current) {
      clearTimeout(pollingTimerRef.current);
    }

    const start = Date.now();
    const TIMEOUT_MS = 12_000;
    const INTERVAL_MS = 800;

    const poll = async (): Promise<void> => {
      try {
        // share-status is written by the backend's ContactShareHandler
        // for every terminal outcome, so polling it lets us distinguish
        // success from "phone already taken" / "user mismatch" instead
        // of treating the latter as a timeout.
        const { status } = await getTelegramShareStatus();
        if (status === 'saved') {
          invalidateUser();
          navigate('/home', { replace: true });
          return;
        }
        if (status === 'phone_taken') {
          setWaitingForContact(false);
          setError(
            t(
              'payoutPhone.phoneTaken',
              'This number is already linked to another account.',
            ),
          );
          return;
        }
        if (status === 'user_mismatch') {
          setWaitingForContact(false);
          setError(
            t(
              'payoutPhone.shareUserMismatch',
              "That contact wasn't your own Telegram number. Try again or enter it manually.",
            ),
          );
          return;
        }
      } catch {
        // Network blip; the next tick will retry.
      }

      if (Date.now() - start >= TIMEOUT_MS) {
        setWaitingForContact(false);
        setError(
          t(
            'payoutPhone.shareTimedOut',
            "We didn't receive your number from Telegram in time. Try again or enter it manually.",
          ),
        );
        return;
      }
      pollingTimerRef.current = setTimeout(poll, INTERVAL_MS);
    };

    void poll();
  };

  return (
    <div className="min-h-screen flex flex-col px-6 py-8 bg-white">
      <h1 className="text-xl font-bold text-gray-900">
        {t('payoutPhone.title', 'Phone number for airtime')}
      </h1>
      <p className="mt-2 text-sm text-gray-600">
        {step === 'enter_phone'
          ? t(
              'payoutPhone.body',
              'We need your phone number to send airtime payouts. We only use it for this purpose.'
            )
          : t('payoutPhone.codeBody', 'We sent a code to {{phone}}. Enter it below.', { phone })}
      </p>

      {step === 'enter_phone' && (
        <form onSubmit={sendCode} className="mt-6 space-y-4">
          <input
            type="tel"
            inputMode="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+251911000000"
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-base focus:outline-none focus:ring-2 focus:ring-offset-1"
            disabled={submitting}
            aria-label={t('payoutPhone.label', 'Phone number')}
            aria-invalid={!!error}
          />

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full py-3 text-sm font-semibold text-white uppercase tracking-wide disabled:opacity-50"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            {submitting ? t('common.loading', 'Loading…') : t('payoutPhone.sendCode', 'Send code')}
          </button>
        </form>
      )}

      {step === 'enter_code' && (
        <form onSubmit={verifyCode} className="mt-6 space-y-4">
          <input
            type="text"
            inputMode="numeric"
            autoComplete="one-time-code"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            placeholder="123456"
            maxLength={8}
            className="w-full rounded-lg border border-gray-300 px-4 py-3 text-base tracking-widest focus:outline-none focus:ring-2 focus:ring-offset-1"
            disabled={submitting}
            aria-label={t('payoutPhone.codeLabel', 'Verification code')}
            aria-invalid={!!error}
          />

          {error && (
            <p className="text-sm text-red-600" role="alert">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full rounded-full py-3 text-sm font-semibold text-white uppercase tracking-wide disabled:opacity-50"
            style={{ backgroundColor: 'var(--brand-primary)' }}
          >
            {submitting ? t('common.loading', 'Loading…') : t('payoutPhone.verify', 'Verify and save')}
          </button>

          <button
            type="button"
            onClick={goBackToPhoneStep}
            disabled={submitting}
            className="w-full rounded-full border border-gray-300 bg-white py-3 text-sm font-semibold text-gray-900 uppercase tracking-wide disabled:opacity-50"
          >
            {t('payoutPhone.changeNumber', 'Use a different number')}
          </button>
        </form>
      )}

      {showInAppShare && step === 'enter_phone' && (
        <div className="mt-6 border-t border-gray-200 pt-6">
          <p className="text-sm text-gray-600">
            {t('payoutPhone.orShareInApp', 'Or share your Telegram-verified number — no SMS code needed.')}
          </p>
          <button
            type="button"
            onClick={handleShareTelegramContact}
            disabled={waitingForContact}
            className="mt-3 w-full rounded-full bg-brand-primary py-3 text-sm font-semibold text-white uppercase tracking-wide disabled:opacity-50"
          >
            {waitingForContact
              ? t('payoutPhone.shareInAppWaiting', 'Waiting for Telegram…')
              : t('payoutPhone.shareInApp', 'Share via Telegram')}
          </button>
        </div>
      )}

      {showShareViaTelegramDM && step === 'enter_phone' && (
        <div className="mt-6 border-t border-gray-200 pt-6">
          <p className="text-sm text-gray-600">
            {t('payoutPhone.orShare', 'Or share your Telegram number directly via the bot.')}
          </p>
          <button
            type="button"
            onClick={() => openTelegramLink(`https://t.me/${botUsername}?start=payout`)}
            className="mt-3 w-full rounded-full border border-gray-300 bg-white py-3 text-sm font-semibold text-gray-900 uppercase tracking-wide"
          >
            {t('payoutPhone.shareViaBot', 'Share via Telegram')}
          </button>
        </div>
      )}
    </div>
  );
}
